import React, { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../config";

function StorageBar({ used, limit, plan, session, documentCount, documentLimit }) {
  const [upgrading, setUpgrading] = useState(false);
  const isPro = plan === "pro";
  const pct = limit ? Math.min(100, (used / limit) * 100) : 0;
  const usedMb = (used / (1024 * 1024)).toFixed(1);
  const limitGb = limit ? (limit / (1024 * 1024 * 1024)).toFixed(0) : null;
  const limitMb = limit && limit < 1024 * 1024 * 1024 ? (limit / (1024 * 1024)).toFixed(0) : null;
  const docLimitReached = !isPro && documentLimit != null && (documentCount ?? 0) >= documentLimit;
  const storageLimitReached = !isPro && pct >= 100;
  const nearStorageLimit = !isPro && pct >= 80 && !storageLimitReached;
  const anyLimitReached = docLimitReached || storageLimitReached;

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const res = await fetch(`${API_BASE}/api/stripe/checkout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || "Could not start checkout. Please try again.");
        return;
      }
      if (data.url) window.location.href = data.url;
    } catch {
      alert("Could not start checkout. Please try again.");
    } finally {
      setUpgrading(false);
    }
  };

  const handleManage = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/stripe/portal`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch {
      alert("Could not open billing portal. Please try again.");
    }
  };

  return (
    <div className="doc-library-storage">
      {/* Limit-exceeded banners */}
      {docLimitReached && (
        <div className="doc-library-limit-banner">
          <strong>Document limit reached</strong> — you've used {documentCount}/{documentLimit} free cloud slots.
          New uploads won't be saved to the cloud until you upgrade or delete a document.
        </div>
      )}
      {storageLimitReached && !docLimitReached && (
        <div className="doc-library-limit-banner">
          <strong>Storage limit reached</strong> — you've used {usedMb} MB of your {limitMb} MB free allowance.
          New uploads won't be saved to the cloud until you upgrade or free up space.
        </div>
      )}

      {/* Storage bar */}
      <div className="doc-library-storage-bar-track">
        <div
          className="doc-library-storage-bar-fill"
          style={{ width: `${pct}%`, background: (storageLimitReached || nearStorageLimit) ? "var(--error)" : "var(--accent)" }}
        />
      </div>

      <div className="doc-library-storage-row">
        <div className="doc-library-storage-stats">
          <span className="doc-library-storage-label" style={{ color: storageLimitReached ? "var(--error)" : undefined }}>
            {isPro
              ? `${usedMb} MB of ${limitGb} GB · Pro`
              : `${usedMb} / ${limitMb} MB storage`}
          </span>
          {!isPro && documentLimit != null && (
            <span className="doc-library-storage-label" style={{ color: docLimitReached ? "var(--error)" : undefined }}>
              {documentCount ?? 0} / {documentLimit} documents
            </span>
          )}
        </div>
        {isPro ? (
          <button type="button" className="doc-library-manage-btn" onClick={handleManage}>
            Manage plan
          </button>
        ) : (
          <button
            type="button"
            className={`doc-library-upgrade-btn${anyLimitReached ? " doc-library-upgrade-btn--urgent" : ""}`}
            onClick={handleUpgrade}
            disabled={upgrading}
          >
            {upgrading ? "Loading…" : anyLimitReached ? "Upgrade to Pro to save more — $3.99/mo" : "Upgrade to Pro — $3.99/mo"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function DocumentLibrary({ session, prefetchedDocs, prefetchedStorage, onOpen, onPdfReady, onClose }) {
  const [docs, setDocs] = useState(prefetchedDocs || []);
  const [storage, setStorage] = useState(prefetchedStorage || null);
  const [loading, setLoading] = useState(!prefetchedDocs);
  const [error, setError] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const authHeaders = useCallback(() => {
    const h = {};
    if (session?.access_token) h["Authorization"] = `Bearer ${session.access_token}`;
    return h;
  }, [session]);

  // Sync if prefetchedDocs arrives after mount (race condition)
  useEffect(() => {
    if (prefetchedDocs) {
      setDocs(prefetchedDocs);
      setLoading(false);
    }
  }, [prefetchedDocs]);

  useEffect(() => {
    // If we already have pre-fetched data, skip the fetch
    if (prefetchedDocs) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Stream documents one-by-one (NDJSON) and fetch storage in parallel
    const streamDocs = async () => {
      const res = await fetch(`${API_BASE}/api/reader/documents`, { headers: authHeaders() });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(`${res.status}: ${b.detail || res.statusText}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let first = true;
      while (true) {
        const { done, value } = await reader.read();
        if (cancelled) { reader.cancel(); return; }
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop(); // keep incomplete last line
        for (const line of lines) {
          if (!line.trim()) continue;
          const doc = JSON.parse(line);
          if (first) { setLoading(false); first = false; }
          setDocs((prev) => [...prev, doc]);
        }
      }
      // flush any remaining line
      if (buffer.trim() && !cancelled) {
        try { setDocs((prev) => [...prev, JSON.parse(buffer)]); } catch {}
      }
      if (first && !cancelled) setLoading(false); // empty list case
    };

    const fetchStorage = fetch(`${API_BASE}/api/reader/storage`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (!cancelled && data) setStorage(data); })
      .catch(() => {});

    setDocs([]); // clear stale docs before streaming fresh ones
    Promise.all([streamDocs(), fetchStorage])
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });

    return () => { cancelled = true; };
  }, [authHeaders, prefetchedDocs]);

  const handleOpen = useCallback(async (fileId) => {
    setOpeningId(fileId);
    try {
      const res = await fetch(`${API_BASE}/api/reader/documents/${fileId}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Failed to load document");
      const doc = await res.json();

      // Open reader immediately with text
      onOpen({
        file_id: doc.file_id,
        filename: doc.filename,
        text: doc.text || "",
        pageBreaks: doc.pageBreaks || [],
        pageWordCounts: doc.page_word_counts || [],
        blobUrl: null,
        pdfUrl: doc.pdf_url || null,
        hasPdf: !!doc.pdf_url,
      });

      // Fetch PDF in background
      if (doc.pdf_url) {
        fetch(doc.pdf_url)
          .then((r) => (r.ok ? r.blob() : null))
          .then((blob) => { if (blob) onPdfReady(doc.file_id, URL.createObjectURL(blob)); else onPdfReady(doc.file_id, null); })
          .catch(() => { onPdfReady(doc.file_id, null); });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setOpeningId(null);
    }
  }, [authHeaders, onOpen, onPdfReady]);

  const handleDelete = useCallback(async (fileId, filename) => {
    if (!window.confirm(`Delete "${filename}" and all its notes from your account?`)) return;
    setDeletingId(fileId);
    try {
      const res = await fetch(`${API_BASE}/api/reader/documents/${fileId}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Delete failed");
      setDocs((prev) => prev.filter((d) => d.file_id !== fileId));
      // Refresh storage info
      fetch(`${API_BASE}/api/reader/storage`, { headers: authHeaders() })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => { if (data) setStorage(data); })
        .catch(() => {});
    } catch (e) {
      setError(e.message);
    } finally {
      setDeletingId(null);
    }
  }, [authHeaders]);

  function fmt(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }

  return (
    <div className="doc-library-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="doc-library-modal" role="dialog" aria-modal="true" aria-label="My Documents">
        <div className="doc-library-header">
          <span className="doc-library-title">My Documents</span>
          <button type="button" className="doc-library-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="doc-library-body">
          {loading && <p className="doc-library-empty">Loading…</p>}
          {error && <p className="doc-library-error">{error}</p>}
          {!loading && !error && docs.length === 0 && (
            <p className="doc-library-empty">No saved documents yet. Upload a PDF while logged in to save it here.</p>
          )}
          {!loading && docs.map((doc) => (
            <div key={doc.file_id} className="doc-library-row">
              <div className="doc-library-info">
                <span className="doc-library-name" title={doc.filename}>{doc.filename}</span>
                <span className="doc-library-meta">
                  {doc.citation_count > 0 && `${doc.citation_count} annotation${doc.citation_count !== 1 ? "s" : ""} · `}
                  {fmt(doc.updated_at)}
                </span>
              </div>
              <div className="doc-library-actions">
                <button
                  type="button"
                  className="doc-library-btn doc-library-btn-open"
                  disabled={openingId === doc.file_id}
                  onClick={() => handleOpen(doc.file_id)}
                >
                  {openingId === doc.file_id ? "Opening…" : "Open"}
                </button>
                <button
                  type="button"
                  className="doc-library-btn doc-library-btn-delete"
                  disabled={deletingId === doc.file_id}
                  onClick={() => handleDelete(doc.file_id, doc.filename)}
                  aria-label={`Delete ${doc.filename}`}
                >
                  {deletingId === doc.file_id ? "…" : "Delete"}
                </button>
              </div>
            </div>
          ))}
        </div>

        {storage && (
          <StorageBar used={storage.used_bytes} limit={storage.limit_bytes} plan={storage.plan} session={session} documentCount={storage.document_count} documentLimit={storage.document_limit} />
        )}
      </div>
    </div>
  );
}
